// The in-headset UI (VR only): the laser pointer, the panels, the menu.
//
// · The LASER: a ray from EITHER controller (its target-ray space, local −Z)
//   with a hit dot. On a target (a panel, the wrist map face, the inspect
//   card) the button under it lights up and the TRIGGER is the
//   click, on release, at the point where the pull began (a flinch of the hand
//   never moves the click). Off every target (owner 2026-11-01) the RIGHT
//   trigger is "the data at the point": its press = ctx.point('right')
//   (main.js vrPoint: a station sign row, else the inspect card of what the
//   ray meets), and the LEFT trigger opens / closes the Map panel
//   (toggleMap). The right world laser + its dot (on the ground / building
//   the ray meets, ctx.worldHit at ≤ WORLD_MS) show while the finger rests on
//   the trigger (touched) and LINGER_MS after, then fade. vrui owns every
//   trigger: update() clears each fire entry (combat is not bound in VR).
// · The PANELS (canvas textures on planes, children of the rig: they walk and
//   turn with the body; each opens in front of the head):
//     MENU — 🗺 Map · ? Help · ⟲ Recenter · Comfort (turn smooth / snap, snap
//            angle) · Beam-in · Picture (Quest) · HUD · ⏏ Exit VR. Right A
//            opens / closes it everywhere (owner 2026-11-03, main.js tick:
//            the wrist ☰ on the left controller was "unusable, too near" and
//            is gone), as does a runtime's menu button (VRControls `menu`, a
//            button past the xr-standard six; the Quest keeps its ☰ to
//            itself). It opens MENU_D 1.25 m ahead, MENU_DOWN_DEG 8° below
//            the eye line, turned to the eyes (Meta: ≥ 1 m for ray UI, avoid
//            0.5–0.8 m; ~1 m for long reading), and stays put on the rig.
//     HELP — this device's controls (no combat rows, as the flat briefing).
//     HUD  — the desktop HUD's twin (VrHud): a compass tape high in view, the
//            status line low (▲ altitude, slope ahead, surface, the zoom,
//            the riding / lift / lake chips, this device's key hints); it
//            follows the head's yaw lazily (past HUD_FOLLOW_DEG, eased),
//            never head-locked. ☰ → HUD on / off (localStorage vrm.vr.hud).
//            The mini map stays on the left wrist (playermap.js).
//     MAP  — PlayerMap's painter (paintPanel), north-up; drag = pan, ＋ / － /
//            ◎ / streets; a pull on a spot = the target marker, a second pull
//            on the marker or "⤳ Beam here" = the beam; a pull on a RED beam
//            spot (meta.beam_spots, combat off) beams there at once (an
//            explicit target, no mark first): ctx.place → net.place,
//            the server's place rules decide (combat off, in bounds, 2 s) —
//            never a local seat. Also opens from the wrist map face and the
//            VR inspect card's 🗺 (PlayerMap.focus → onVrMap); the left
//            trigger off every target and the left stick click open / close
//            it (toggleMap), B closes the top panel (back).
// · COMFORT: the right stick's turn (main.js tick) — smooth (default,
//   tunables locomotion.vr_snap_turn false, at stick_turn_rate_deg) or snap
//   (snap_turn_deg, one of snap_turn_choices_deg); the user's pick persists
//   in localStorage (vrm.vr.turn, vrm.vr.snap_deg). The beam-in landing:
//   fly down (the VR landing) or instant (the black wait) — the player's pick
//   (vrm.vr.beam; beamflight.js vrBeam) over the world's tunable flight.vr,
//   from the next beam-in on (main.js enterAt).
// Perf: no per-frame allocation; a panel repaints only when it changed
// (hover, a click, a status), the map at most every MAP_MS (MAP_DRAG_MS while
// dragging) — the wrist's WRIST_MS-style throttle; the ray test is analytic
// against each visible plane (no Raycaster, no scene walk).
import * as THREE from 'three';
import { Sfx } from './audio.js';
import { paintCompass } from './scopehud.js';
import { spotAt } from './playermap.js';
import { t } from './i18n.js';
import './i18n/player.js';
import { vrBeam } from './beamflight.js';
import { HintCycle, KH, khReduced } from './keyhints.js';
import { CtrlWatch } from './headseat.js';

const LS_TURN = 'vrm.vr.turn', LS_SNAP = 'vrm.vr.snap_deg', LS_HUD = 'vrm.vr.hud', LS_BEAM = 'vrm.vr.beam';
const MAP_MS = 200, MAP_DRAG_MS = 80;   // map panel repaint periods
const DRAG_PX = 24;          // canvas px of ray travel from the down point before a press pans (the tap slop)
const MARK_HIT_PX = 48;      // a second pull this close to the marker = beam (and to a red beam spot)
const RAY_IDLE_M = 1.5;      // the laser's length with a panel open and nothing hit
const WORLD_MS = 100;        // the world laser's hit, per hand, at most this often
const WORLD_SKY_M = 40;      // the world laser's length when the ray meets nothing
const LINGER_MS = 1200;      // the world laser stays this long after the finger leaves the trigger
const DOT_K = 0.006;         // the world dot's radius per metre of distance (≈ 0.35°, a constant angular size)
const HUD_D = 1.8;           // the HUD panels' distance from the eyes, m
// the menu + help placement (Meta Horizon design "Display" + "Hands UI best practices": ray UI ≥ 1 m, avoid
// 0.5–0.8 m; Android XR "Spatial UI": the centre ~5° below the eye line) — angular sizes in the comments
const MENU_D = 1.25, MENU_DOWN_DEG = 8;      // 0.64 m wide: ≈ 29° × 40° (with the Picture row), 30 px text ≈ 1.1°
const HELP_D = 1.3, HELP_DOWN_DEG = 6;       // 1.0 m wide: ≈ 42° × 44°, 25 px text ≈ 1.1°
const MENU_DROP = MENU_D * Math.tan(MENU_DOWN_DEG * Math.PI / 180);
const HELP_DROP = HELP_D * Math.tan(HELP_DOWN_DEG * Math.PI / 180);
const HUD_UP_DEG = 15, HUD_DOWN_DEG = 24;   // compass above / status below the eye line
const HUD_FOLLOW_DEG = 18, HUD_SETTLE_DEG = 2;   // the lazy yaw follow: starts past, stops under
const COMPASS_MS = 66, STATUS_MS = 120;     // HUD repaint periods (the wrist map's WRIST_MS-style throttle)
const MPP_MIN = 0.5;         // map zoom: metres per canvas px, nearest
const FONT = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const HANDS = ['left', 'right'];

const _inv = new THREE.Matrix4();
const _o = new THREE.Vector3(), _d = new THREE.Vector3(), _p = new THREE.Vector3();
const _ro = new THREE.Vector3(), _rd = new THREE.Vector3();
const _e = new THREE.Vector3(), _f = new THREE.Vector3();

function lsGet(k) { try { return localStorage.getItem(k); } catch { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, String(v)); } catch { /* private mode */ } }

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  g.moveTo(x + r, y); g.arcTo(x + w, y, x + w, y + h, r); g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r); g.arcTo(x, y, x + w, y, r); g.closePath();
}

// one plane + its canvas; buttons are canvas-px boxes, rebuilt by each paint
class Panel {
  constructor(wPx, hPx, wM, order = 30) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = wPx; this.canvas.height = hPx;
    this.g = this.canvas.getContext('2d');
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.SRGBColorSpace;
    this.wPx = wPx; this.hPx = hPx; this.wM = wM; this.hM = wM * hPx / wPx;
    // in-world UI: outside the display curve, over the world (never cut by a wall)
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(this.wM, this.hM),
      new THREE.MeshBasicMaterial({ map: this.tex, transparent: true, depthTest: false, depthWrite: false,
        toneMapped: false, fog: false }));
    this.mesh.renderOrder = order;
    this.mesh.visible = false;
    this.buttons = [];       // { id, x, y, w, h, label, sel, off }
    this.hover = null;       // the id under a laser (painted lit)
    this.hoverNow = null;    // this frame's, folded in by update()
    this.dirty = true;
  }
  get open() { return this.mesh.visible; }
  // a taller canvas (the same width in metres): a new texture of the new size (three allocates a
  // texture's storage once) and the plane grown downward from the same top edge
  resize(hPx) {
    if (hPx === this.hPx) return;
    const top = this.mesh.position.y + this.hM / 2, map = this.tex;
    this.canvas.height = hPx; this.hPx = hPx; this.hM = this.wM * hPx / this.wPx;
    this.tex = new THREE.CanvasTexture(this.canvas); this.tex.colorSpace = THREE.SRGBColorSpace;
    this.mesh.material.map = this.tex; this.mesh.material.needsUpdate = true; map.dispose();
    this.mesh.geometry.dispose(); this.mesh.geometry = new THREE.PlaneGeometry(this.wM, this.hM);
    this.mesh.position.y = top - this.hM / 2;
    this.dirty = true;
  }
  button(px, py) {
    for (const b of this.buttons) if (!b.off && px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h) return b;
    return null;
  }
  // a button: rounded, lit under the laser, gold when selected, dim when off
  drawButton(id, x, y, w, h, label, o = {}) {
    const g = this.g, b = { id, x, y, w, h, label, sel: !!o.sel, off: !!o.off };
    this.buttons.push(b);
    roundRect(g, x, y, w, h, Math.min(18, h / 3));
    g.fillStyle = b.off ? 'rgba(40,48,62,.7)' : this.hover === id ? '#3b82f6' : b.sel ? '#a16207' : 'rgba(51,65,85,.95)';
    g.fill();
    g.lineWidth = 3; g.strokeStyle = this.hover === id && !b.off ? '#bfdbfe' : b.sel ? '#ffc93c' : 'rgba(148,163,184,.6)';
    g.stroke();
    g.fillStyle = b.off ? 'rgba(203,213,225,.45)' : '#fff';
    g.font = `600 ${o.size || 34}px ${FONT}`;
    g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(label, x + w / 2, y + h / 2 + 1);
    return b;
  }
  background(title) {
    const g = this.g, W = this.wPx, H = this.hPx;
    this.buttons.length = 0;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, W, H);
    roundRect(g, 4, 4, W - 8, H - 8, 28);
    g.fillStyle = 'rgba(15,23,42,.94)'; g.fill();
    g.lineWidth = 4; g.strokeStyle = 'rgba(148,163,184,.55)'; g.stroke();
    if (title) {
      g.fillStyle = '#e2e8f0'; g.font = `700 40px ${FONT}`;
      g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(title, 36, 52);
    }
  }
}

// the in-headset help: what this device does (every row is a binding in
// player.js VRControls / main.js tick / this file; no combat rows) — built
// in the current language at paint time (never at module load)
function helpRows() {
  return [
    [t('player.vr.help.left'), [
      [t('player.vr.help.k.stick'), t('player.vr.help.walk')],
      [t('player.vr.help.k.stickClick'), t('player.vr.help.mapToggle')],
      ['X', t('player.vr.help.bikeOnOff')],
      ['Y', t('player.vr.help.mic')],
      [t('player.vr.help.k.grip'), t('player.vr.help.climbRock')],
      [t('player.vr.help.k.trigger'), t('player.vr.help.triggerMap')],
    ]],
    [t('player.vr.help.right'), [
      [t('player.vr.help.k.stickUD'), t('player.vr.help.zoom')],
      [t('player.vr.help.k.stickLR'), t('player.vr.help.turn')],
      [t('player.vr.help.k.stickClick'), t('player.vr.help.jump')],
      ['A', t('player.vr.help.menuA')],
      ['B', t('player.vr.help.closeB')],
      [t('player.vr.help.k.grip'), t('player.vr.help.climb')],
      [t('player.vr.help.k.trigger'), t('player.vr.help.trigger')],
    ]],
    [t('player.vr.help.bike'), [
      [t('player.vr.help.k.leftStick'), t('player.vr.help.throttle')],
      [t('player.vr.help.k.jetsuit'), t('player.vr.help.jetsuit')],
    ]],
    [t('player.vr.help.pointing'), [
      [t('player.vr.help.k.finger'), t('player.vr.help.laser')],
      [t('player.vr.help.k.wrist'), t('player.vr.help.wrist')],
      [t('player.vr.help.k.map'), t('player.vr.help.mapMark')],
      [t('player.vr.help.k.mapRed'), t('player.vr.help.mapRed')],
    ]],
  ];
}

export class VrUi {
  // ctx: { rig, head (the rig-local camera), eye() (the XR camera), vr()
  //   (VRControls), ctrl(hand), playerMap(), self() → {x, z, heading}|null,
  //   combatOn(), place(x, z) → Promise<pos|null>, blocked(x, z),
  //   extent() → {x, z}|null, groundY(x, z), recenter(), exitVr(),
  //   point(hand) (a trigger press off every target), canPoint(),
  //   worldHit(origin, dir) → metres | null, card() → {mesh, wPx, hPx} | null
  //   (the inspect card), cardClick(px, py), zoom() → the view zoom (HUD) }
  constructor(ctx) {
    this.ctx = ctx;
    this.turn = 'smooth';            // the right stick's turn: 'smooth' | 'snap'
    this.snapDeg = 30;
    this.snapChoices = [30, 45];
    this.beamPick = null;            // the beam-in landing: 'fly' | 'instant' | null (the world's flight.vr)
    this._flightT = null;
    this.menu = new Panel(768, 940, 0.64);
    this.help = new Panel(1024, 1080, 1.0);
    this.map = new Panel(1024, 768, 1.1);
    this.panels = [this.menu, this.help, this.map];
    for (const p of this.panels) ctx.rig.add(p.mesh);
    this._all = [this.menu, this.help, this.map];
    // the map view (north-up) + its marker and status
    this._mv = { cx: 0, cz: 0, mpp: 3, rot: 0, round: false, names: true, stations: true, px: 2, streets: false, north: true };
    this._mark = null;               // { x, z } the beam target
    this._status = '';
    this._beaming = false;
    this._mapAt = 0;
    this._dragging = false;
    // per hand: the laser (a beam + a dot, children of the controller) and the press
    this._h = {};
    for (const hand of HANDS) {
      const grp = new THREE.Group();
      const beam = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.004, 1).translate(0, 0, -0.5),
        new THREE.MeshBasicMaterial({ color: 0x67e8f9, transparent: true, opacity: 0.85, depthTest: false,
          depthWrite: false, toneMapped: false, fog: false }));
      beam.renderOrder = 29;
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.007, 10, 8),
        new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false, depthWrite: false, toneMapped: false, fog: false }));
      dot.renderOrder = 32;
      grp.add(beam, dot);
      grp.visible = false;
      this._h[hand] = { grp, beam, dot, was: false, down: null, hit: null, px: 0, py: 0, lastPx: 0, lastPy: 0, drag: false,
        world: false, aimAt: -1e9, worldAt: -1e9, worldDist: null, op: 0 };
    }
    // the ray's nearest target this frame (reused): { t (a Panel | the wrist face), px, py, dist }
    this._hitOut = { t: null, px: 0, py: 0, dist: 0 };
    this._hitAny = false;
    this._wristFace = { mesh: null, wPx: 1, hPx: 1, wM: 0, hM: 0, open: true };
    this._card = { mesh: null, wPx: 1, hPx: 1, wM: 1, hM: 1, open: true };   // the inspect card (ctx.card)
    this.hud = new VrHud(ctx);
    // "Controllers asleep" (vr-redon): head-locked, a little below the eye
    // line, while the session has no controller source (headseat.js CtrlWatch)
    this.ctrlHint = new Panel(1024, 120, 0.62, 34);
    this.ctrlHint.mesh.position.set(0, -0.22, -1.1);
    ctx.head.add(this.ctrlHint.mesh);
    this._ctrlW = new CtrlWatch();
  }

  // inp.ctrl = this poll's controller sources (VRControls.poll); repainted
  // once a showing (the current language)
  _ctrlHintTick(n, now) {
    const on = this._ctrlW.update(n, now), p = this.ctrlHint;
    if (on === p.mesh.visible) return;
    if (on) {
      p.background('');
      const g = p.g;
      g.fillStyle = '#fde68a'; g.font = `600 40px ${FONT}`;
      g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(t('player.vr.ctrlAsleep'), p.wPx / 2, p.hPx / 2 + 1, p.wPx - 48);
      p.tex.needsUpdate = true;
    }
    p.mesh.visible = on;
  }

  applyTunables(t) {
    const L = (t && t.locomotion) || {};
    const ch = Array.isArray(L.snap_turn_choices_deg) ? L.snap_turn_choices_deg.filter((d) => d > 0 && d <= 90) : [];
    this.snapChoices = ch.length ? ch : [30, 45];
    const lt = lsGet(LS_TURN);
    this.turn = lt === 'snap' || lt === 'smooth' ? lt : (L.vr_snap_turn ? 'snap' : 'smooth');
    const ld = Number(lsGet(LS_SNAP));
    this.snapDeg = this.snapChoices.includes(ld) ? ld : (L.snap_turn_deg > 0 ? L.snap_turn_deg : this.snapChoices[0]);
    const lb = lsGet(LS_BEAM);
    this.beamPick = lb === 'fly' || lb === 'instant' ? lb : null;
    this._flightT = (t && t.flight) || null;
    const vr = this.ctx.vr && this.ctx.vr();
    if (vr) {
      if (L.snap_turn_arm > 0) vr.snapArm = L.snap_turn_arm;
      if (L.snap_turn_rearm > 0) vr.snapRearm = L.snap_turn_rearm;
    }
    this.menu.dirty = true; this.help.dirty = true;
  }

  get anyOpen() { return this.menu.open || this.help.open || this.map.open; }

  // the left trigger (off every target) or the left stick click: the Map panel open ⇄ closed
  toggleMap() {
    if (this.map.open) { this.map.mesh.visible = false; this._mark = null; this._status = ''; }
    else this.openMap();
    Sfx.click && Sfx.click();
  }

  // B: close the top panel (help → the menu, the rest closed)
  back() {
    if (this.help.open) { this.help.mesh.visible = false; this._show(this.menu, MENU_D, MENU_DROP); return; }
    if (this.map.open) { this.map.mesh.visible = false; this._mark = null; this._status = ''; return; }
    this.menu.mesh.visible = false;
  }

  // ------------------------------------------------------------ open / close
  closeAll() {
    for (const p of this.panels) p.mesh.visible = false;
    for (const hand of HANDS) {
      const s = this._h[hand];
      s.grp.visible = false; s.down = null; s.was = false; s.world = false; s.op = 0; s.worldDist = null;
    }
    this.hud.hide();
    this.ctrlHint.mesh.visible = false; this._ctrlW.reset();
    this._mark = null; this._status = ''; this._dragging = false;
  }

  // right A (main.js tick) or a runtime's menu button: the menu open ⇄ closed (an open Map gives way to it)
  toggleMenu() {
    if (this.menu.open || this.help.open) { this.menu.mesh.visible = false; this.help.mesh.visible = false; }
    else { this.map.mesh.visible = false; this._show(this.menu, MENU_D, MENU_DROP); }
    Sfx.click && Sfx.click();
  }

  // the Map panel, centred on (x, z) (a card's 🗺) or on you
  openMap(x, z) {
    const s = this.ctx.self();
    const mv = this._mv;
    if (x !== undefined && z !== undefined) { mv.cx = x; mv.cz = z; mv.mpp = Math.min(mv.mpp, 2); this._mark = { x, z }; }
    else if (s) { mv.cx = s.x; mv.cz = s.z; }
    this._status = '';
    this._clampView();
    this.menu.mesh.visible = false; this.help.mesh.visible = false;
    this._show(this.map, 1.0, 0.18);
    return true;
  }

  // in front of the head (yaw only), a little below the eye, facing it
  _show(p, dist, drop) {
    const head = this.ctx.head;
    _f.set(0, 0, -1).applyQuaternion(head.quaternion);
    _f.y = 0;
    if (_f.lengthSq() < 1e-4) _f.set(0, 0, -1);
    _f.normalize();
    p.mesh.position.copy(head.position).addScaledVector(_f, dist);
    p.mesh.position.y -= drop;
    head.getWorldPosition(_e);
    p.mesh.lookAt(_e);
    p.mesh.visible = true;
    p.hover = null;
    p.dirty = true;
  }

  // --------------------------------------------------------------- the frame
  // once a VR tick, right after VRControls.poll: lasers, hover, clicks, the
  // world point, the HUD. Every trigger is owned here: inp.fire is cleared.
  update(inp, now, dt = 1 / 72) {
    const c = this.ctx;
    if (inp.menu) this.toggleMenu();
    this._ctrlHintTick(inp.ctrl, now);
    const pm = c.playerMap && c.playerMap();
    const face = pm ? pm.wristFace : null;
    this._wristFace.mesh = face ? face.mesh : null;
    this._wristFace.wM = this._wristFace.hM = face ? face.size : 0;
    const card = c.card ? c.card() : null;
    this._card.mesh = card ? card.mesh : null;
    if (card) { this._card.wPx = card.wPx; this._card.hPx = card.hPx; }
    const canPoint = !!(c.canPoint && c.canPoint());
    for (const p of this._all) p.hoverNow = null;
    for (const f of inp.fire) {
      if (f.hand !== 'left' && f.hand !== 'right') { f.pressed = false; continue; }
      const s = this._h[f.hand];
      const ctrl = c.ctrl(f.hand);
      if (!ctrl) { f.pressed = false; continue; }
      if (s.grp.parent !== ctrl) ctrl.add(s.grp);
      ctrl.updateWorldMatrix(true, false);
      _ro.setFromMatrixPosition(ctrl.matrixWorld);
      _rd.set(0, 0, -1).transformDirection(ctrl.matrixWorld);
      const h = this._hit(_ro, _rd);
      s.hit = h ? h.t : null;
      if (h) { s.px = h.px; s.py = h.py; }
      const pressed = f.pressed;
      if (inp.wake) s.was = pressed;            // the press that woke a controller does nothing else
      if (pressed && !s.was && h) {
        s.down = { t: h.t, px: h.px, py: h.py };
        s.lastPx = h.px; s.lastPy = h.py; s.drag = false;
      } else if (pressed && !s.was && !h && canPoint && f.hand === 'left') {
        this.toggleMap();                      // the left trigger: the Map panel open ⇄ closed
      } else if (pressed && !s.was && !h && canPoint && !this.anyOpen) {
        s.aimAt = now;
        if (c.point) c.point(f.hand);          // the right trigger: the data at the point (main.js vrPoint)
      } else if (pressed && s.down && h && h.t === s.down.t && s.down.t === this.map) {
        this._mapDrag(s, h);
      } else if (!pressed && s.was && s.down) {
        const d = s.down;
        s.down = null;
        if (!s.drag) this._click(d.t, d.px, d.py);
        if (s.drag) { this._dragging = false; this.map.dirty = true; }
      }
      s.was = pressed;
      if (h && h.t.hoverNow === null) {
        const b = h.t.button ? h.t.button(h.px, h.py) : null;
        h.t.hoverNow = b ? b.id : null;
      }
      f.pressed = false;                        // vrui owns every trigger (no shot in VR)
      if (f.touched) s.aimAt = now;
      // the laser: on a target or while a panel is open (both hands) at full
      // strength; else on the world while aiming, fading out after LINGER_MS
      if (h || this.anyOpen) {
        s.world = false;
        s.op = 1;
        s.grp.visible = true;
        const len = h ? h.dist : RAY_IDLE_M;
        s.beam.scale.set(1, 1, Math.max(0.01, len));
        s.beam.material.opacity = h ? 0.9 : 0.35;
        s.dot.visible = !!h;
        s.dot.position.set(0, 0, -len);
        s.dot.scale.setScalar(1);
        continue;
      }
      // the world laser is the right hand's (the left trigger toggles the Map)
      const aiming = canPoint && f.hand === 'right' && now - s.aimAt < LINGER_MS;
      s.op += ((aiming ? 1 : 0) - s.op) * Math.min(1, dt * (aiming ? 12 : 4));
      if (s.op < 0.02) { s.op = 0; s.grp.visible = false; s.world = false; s.worldDist = null; continue; }
      s.grp.visible = true;
      s.world = true;
      if (aiming && now - s.worldAt >= WORLD_MS && c.worldHit) {
        s.worldAt = now;
        s.worldDist = c.worldHit(_ro, _rd);
      }
      const wd = s.worldDist;
      const len = wd !== null && wd !== undefined && Number.isFinite(wd) ? wd : WORLD_SKY_M;
      s.beam.scale.set(1, 1, Math.max(0.01, len));
      s.beam.material.opacity = 0.5 * s.op;
      s.dot.visible = len !== WORLD_SKY_M;
      s.dot.position.set(0, 0, -len);
      s.dot.scale.setScalar(Math.max(1, len * DOT_K / 0.007));
    }
    for (const p of this._all) {
      if (p.hover !== p.hoverNow) { p.hover = p.hoverNow; p.dirty = true; }
    }
    this._paint(now);
    this.hud.update(now, dt);
  }

  // the nearest visible target the ray meets, front side only → _hitOut | null
  _hit(ori, dir) {
    this._hitAny = false;
    for (const p of this.panels) if (p.open) this._test(p, p.mesh, p.wM, p.hM, p.wPx, p.hPx, ori, dir);
    const wf = this._wristFace;
    if (wf.mesh && wf.mesh.visible && wf.mesh.material.opacity > 0.5) this._test(wf, wf.mesh, wf.wM, wf.hM, 1, 1, ori, dir);
    const cd = this._card;
    if (cd.mesh && cd.mesh.visible) this._test(cd, cd.mesh, 1, 1, cd.wPx, cd.hPx, ori, dir);
    return this._hitAny ? this._hitOut : null;
  }

  // the ray vs one centred plane (local z = 0, facing +z), nearer than the best so far
  _test(t, mesh, wM, hM, wPx, hPx, ori, dir) {
    const out = this._hitOut;
    mesh.updateWorldMatrix(true, false);
    _inv.copy(mesh.matrixWorld).invert();
    _o.copy(ori).applyMatrix4(_inv);
    _d.copy(dir).transformDirection(_inv);
    if (_o.z <= 0 || _d.z >= -1e-6) return;
    const k = -_o.z / _d.z;
    const x = _o.x + _d.x * k, y = _o.y + _d.y * k;
    if (Math.abs(x) > wM / 2 || Math.abs(y) > hM / 2) return;
    _p.set(x, y, 0).applyMatrix4(mesh.matrixWorld);
    const dist = _p.distanceTo(ori);
    if (this._hitAny && dist >= out.dist) return;
    this._hitAny = true;
    out.t = t; out.dist = dist;
    out.px = (x / wM + 0.5) * wPx; out.py = (0.5 - y / hM) * hPx;
  }

  // ----------------------------------------------------------------- clicks
  _click(tg, px, py) {
    if (tg === this._wristFace) { this.openMap(); Sfx.hitConfirm(); return; }
    if (tg === this._card) { if (this.ctx.cardClick) this.ctx.cardClick(px, py); return; }
    const b = tg.button(px, py);
    if (tg === this.map) { this._mapClick(b, px, py); return; }
    if (!b) return;
    Sfx.hitConfirm();
    const c = this.ctx;
    switch (b.id) {
      case 'map': this.openMap(); break;
      case 'help': this.menu.mesh.visible = false; this._show(this.help, HELP_D, HELP_DROP); break;
      case 'back': this.help.mesh.visible = false; this._show(this.menu, MENU_D, MENU_DROP); break;
      case 'recenter':
        this._status = c.recenter() ? t('player.vr.status.recentred') : t('player.vr.status.noRecenter');
        this.menu.dirty = true;
        break;
      case 'hud':
        this.hud.setOn(!this.hud.on); this.menu.dirty = true; break;
      case 'smooth': case 'snap':
        this.turn = b.id; lsSet(LS_TURN, b.id); this.menu.dirty = true; break;
      case 'beamFly': case 'beamInstant':
        this.beamPick = b.id === 'beamFly' ? 'fly' : 'instant'; lsSet(LS_BEAM, this.beamPick);
        this._status = t('player.vr.status.beamNext');
        this.menu.dirty = true;
        break;
      case 'exit': this.closeAll(); c.exitVr(); break;
      case 'qp:current': case 'qp:balanced': case 'qp:steady':
        if (c.setPicture && c.setPicture(b.id.slice(3))) this._status = t(`player.vr.status.qp.${b.id.slice(3)}`);
        this.menu.dirty = true;
        break;
      case 'close': this.menu.mesh.visible = false; this.help.mesh.visible = false; break;
      default:
        if (b.id.startsWith('deg')) {
          this.snapDeg = Number(b.id.slice(3)); lsSet(LS_SNAP, this.snapDeg);
          this.turn = 'snap'; lsSet(LS_TURN, 'snap');
          this.menu.dirty = true;
        }
    }
  }

  _mapClick(b, px, py) {
    const mv = this._mv;
    if (b) {
      Sfx.hitConfirm();
      switch (b.id) {
        case 'zin': mv.mpp /= 1.6; break;
        case 'zout': mv.mpp *= 1.6; break;
        case 'me': { const s = this.ctx.self(); if (s) { mv.cx = s.x; mv.cz = s.z; } break; }
        case 'layer': mv.streets = !mv.streets; break;
        case 'close': this.map.mesh.visible = false; this._mark = null; this._status = ''; return;
        case 'beam': this._beam(); break;
        case 'unmark': this._mark = null; this._status = ''; break;
        default: break;
      }
      this._clampView();
      this.map.dirty = true; this._mapAt = 0;
      return;
    }
    // the map itself: a red beam spot beams at once; a second pull on the
    // marker beams, else a new marker
    const W = this.map.wPx, H = this.map.hPx;
    const pm = this.ctx.playerMap && this.ctx.playerMap();
    const spot = pm && mv.spots ? spotAt(pm.spots, mv, W, H, px, py, MARK_HIT_PX) : null;
    if (spot) { this._mark = { x: spot.x, z: spot.z }; this._status = ''; this._beam(); return; }
    const m = this._mark;
    if (m) {
      const sx = W / 2 + (m.x - mv.cx) / mv.mpp, sy = H / 2 + (m.z - mv.cz) / mv.mpp;
      if (Math.hypot(sx - px, sy - py) < MARK_HIT_PX) { this._beam(); return; }
    }
    const x = mv.cx + (px - W / 2) * mv.mpp, z = mv.cz + (py - H / 2) * mv.mpp;
    const ex = this.ctx.extent();
    if (ex && (x < 0 || z < 0 || x > ex.x || z > ex.z)) { Sfx.uiDeny(); this._status = t('player.vr.status.outside'); }
    else { this._mark = { x, z }; this._status = ''; Sfx.hitConfirm(); }
    this.map.dirty = true; this._mapAt = 0;
  }

  _mapDrag(s, h) {
    if (!s.drag && Math.hypot(h.px - s.down.px, h.py - s.down.py) < DRAG_PX) return;
    if (!s.drag) { s.drag = true; s.lastPx = s.down.px; s.lastPy = s.down.py; this._dragging = true; }
    const mv = this._mv;
    mv.cx -= (h.px - s.lastPx) * mv.mpp; mv.cz -= (h.py - s.lastPy) * mv.mpp;
    s.lastPx = h.px; s.lastPy = h.py;
    this._clampView();
    this.map.dirty = true;
  }

  _clampView() {
    const mv = this._mv, ex = this.ctx.extent();
    if (!ex) return;
    const W = this.map.wPx, H = this.map.hPx;
    const mppMax = Math.max(ex.x / W, ex.z / H) * 1.15;
    mv.mpp = Math.max(MPP_MIN, Math.min(mppMax, mv.mpp));
    mv.cx = Math.max(0, Math.min(ex.x, mv.cx)); mv.cz = Math.max(0, Math.min(ex.z, mv.cz));
  }

  // the beam: a step out of a footprint / a lake (16 bearings, 1 m rings, the
  // Map card's rule with no eye), then the SERVER's place
  _beam() {
    const c = this.ctx, m = this._mark;
    if (!m || this._beaming) return;
    if (c.combatOn()) { Sfx.uiDeny(); this._status = t('player.vr.status.combatNoBeam'); this.map.dirty = true; return; }
    let x = m.x, z = m.z;
    if (c.blocked(x, z)) {
      out: for (let s = 1; s < 400; s++) {
        for (let k = 0; k < 16; k++) {
          const a = k * Math.PI / 8, ux = Math.sin(a), uz = -Math.cos(a);
          if (c.blocked(x + ux * s, z + uz * s)) continue;
          x += ux * (s + 2); z += uz * (s + 2);
          break out;
        }
      }
    }
    const ex = c.extent();
    if (ex && !(x >= 2 && z >= 2 && x <= ex.x - 2 && z <= ex.z - 2)) { Sfx.uiDeny(); this._status = t('player.vr.status.outside'); this.map.dirty = true; return; }
    this._beaming = true;
    this._status = t('player.vr.status.beaming');
    this.map.dirty = true; this._mapAt = 0;
    Promise.resolve(c.place(x, z)).then((pos) => {
      this._beaming = false;
      if (!pos) {
        Sfx.uiDeny();
        this._status = t('player.vr.status.refused');
      } else {
        Sfx.hitConfirm();
        this._mark = null; this._status = '';
        this.map.mesh.visible = false;
      }
      this.map.dirty = true; this._mapAt = 0;
    }, () => { this._beaming = false; this._status = t('player.vr.status.noReply'); this.map.dirty = true; });
  }

  // ----------------------------------------------------------------- paint
  _paint(now) {
    if (this.menu.open && this.menu.dirty) this._paintMenu();
    if (this.help.open && this.help.dirty) this._paintHelp();
    if (this.map.open) {
      // the others' dots move: a slow repaint even when nothing changed
      const per = this._dragging ? MAP_DRAG_MS : MAP_MS;
      if ((this.map.dirty || now - this._mapAt > MAP_MS * 5) && now - this._mapAt >= per) {
        this._mapAt = now;
        this._paintMap();
      }
    }
  }

  _paintMenu() {
    const p = this.menu, g = p.g, W = p.wPx;
    if (this.ctx.picture && this.ctx.picture() && p.hPx < 1070) p.resize(1070);   // the Picture row (Quest)
    p.background(t('player.vr.menu.title'));
    p.drawButton('close', W - 96, 20, 64, 64, '×', { size: 44 });
    const x0 = 36, w = W - 72;
    p.drawButton('map', x0, 110, w, 84, t('player.vr.menu.map'));
    p.drawButton('help', x0, 210, (w - 20) / 2, 84, t('player.vr.menu.help'));
    p.drawButton('recenter', x0 + (w + 20) / 2, 210, (w - 20) / 2, 84, t('player.vr.menu.recenter'));
    g.fillStyle = '#cbd5e1'; g.font = `600 30px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle';
    g.fillText(t('player.vr.menu.comfort'), x0, 336);
    p.drawButton('smooth', x0, 368, (w - 20) / 2, 76, t('player.vr.menu.smooth'), { sel: this.turn === 'smooth' });
    p.drawButton('snap', x0 + (w + 20) / 2, 368, (w - 20) / 2, 76, t('player.vr.menu.snap'), { sel: this.turn === 'snap' });
    const n = this.snapChoices.length, bw = (w - 20 * (n - 1)) / n;
    this.snapChoices.forEach((d, i) => p.drawButton(`deg${d}`, x0 + i * (bw + 20), 458, bw, 70, `${d}°`,
      { sel: this.turn === 'snap' && this.snapDeg === d, size: 32 }));
    const fly = vrBeam(this.beamPick, this._flightT) === 'flight';
    g.fillStyle = '#cbd5e1'; g.font = `600 30px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle';
    g.fillText(t('player.vr.menu.beam'), x0, 566);
    p.drawButton('beamFly', x0, 598, (w - 20) / 2, 70, t('player.vr.menu.beamFly'), { sel: fly, size: 30 });
    p.drawButton('beamInstant', x0 + (w + 20) / 2, 598, (w - 20) / 2, 70, t('player.vr.menu.beamInstant'), { sel: !fly, size: 30 });
    // the Quest picture profile (questprof.js; quest-steady72): one row, only where the class has profiles
    const pic = this.ctx.picture ? this.ctx.picture() : null, dy = pic ? 130 : 0;
    if (pic) {
      g.fillStyle = '#cbd5e1'; g.font = `600 30px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(t('player.vr.menu.picture'), x0, 702);
      const ids = ['current', 'balanced', 'steady'], pw = (w - 40) / 3;
      ids.forEach((id, i) => p.drawButton(`qp:${id}`, x0 + i * (pw + 20), 734, pw, 70, t(`player.vr.menu.qp.${id}`), { sel: pic === id, size: 28 }));
    }
    p.drawButton('hud', x0, 700 + dy, w, 76, this.hud.on ? t('player.vr.menu.hudOn') : t('player.vr.menu.hudOff'),
      { sel: this.hud.on, size: 30 });
    p.drawButton('exit', x0, 800 + dy, w, 84, t('player.vr.menu.exit'));
    if (this._status && !this.map.open) {
      g.fillStyle = '#93c5fd'; g.font = `500 26px ${FONT}`; g.textAlign = 'center';
      g.fillText(this._status, W / 2, 912 + dy);
    }
    p.tex.needsUpdate = true;
    p.dirty = false;
  }

  _paintHelp() {
    const p = this.help, g = p.g, W = p.wPx;
    p.background(t('player.vr.help.title'));
    p.drawButton('back', W - 260, 20, 140, 64, t('player.vr.menu.back'), { size: 30 });
    p.drawButton('close', W - 100, 20, 64, 64, '×', { size: 44 });
    let y = 120;
    for (const [head, rows] of helpRows()) {
      g.fillStyle = '#ffc93c'; g.font = `700 30px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(head, 36, y);
      y += 40;
      for (const [k, v] of rows) {
        g.font = `700 25px ${FONT}`;
        const kw = g.measureText(k).width + 24;
        roundRect(g, 48, y - 17, kw, 34, 8);
        g.fillStyle = 'rgba(51,65,85,.95)'; g.fill();
        g.fillStyle = '#fff'; g.fillText(k, 60, y + 1);
        g.fillStyle = '#e2e8f0'; g.font = `500 25px ${FONT}`;
        g.fillText(v, 64 + Math.max(kw, 170), y + 1);
        y += 38;
      }
      y += 10;
    }
    p.tex.needsUpdate = true;
    p.dirty = false;
  }

  _paintMap() {
    const p = this.map, g = p.g, W = p.wPx, H = p.hPx, mv = this._mv, c = this.ctx;
    const pm = c.playerMap && c.playerMap();
    p.buttons.length = 0;
    g.setTransform(1, 0, 0, 1, 0, 0);
    mv.spots = !c.combatOn();       // the red beam spots (drawn + a pull = beam): combat off
    if (pm) pm.paintPanel(g, W, H, mv);
    else { g.fillStyle = '#18202c'; g.fillRect(0, 0, W, H); }
    g.setTransform(1, 0, 0, 1, 0, 0);
    // the marker: a gold ring + cross where the beam would go
    const m = this._mark;
    if (m) {
      const sx = W / 2 + (m.x - mv.cx) / mv.mpp, sy = H / 2 + (m.z - mv.cz) / mv.mpp;
      g.lineWidth = 5; g.strokeStyle = '#000';
      g.beginPath(); g.arc(sx, sy, 22, 0, Math.PI * 2); g.stroke();
      g.lineWidth = 3.5; g.strokeStyle = '#ffc93c';
      g.beginPath(); g.arc(sx, sy, 22, 0, Math.PI * 2); g.stroke();
      g.beginPath(); g.moveTo(sx - 34, sy); g.lineTo(sx - 10, sy); g.moveTo(sx + 10, sy); g.lineTo(sx + 34, sy);
      g.moveTo(sx, sy - 34); g.lineTo(sx, sy - 10); g.moveTo(sx, sy + 10); g.lineTo(sx, sy + 34); g.stroke();
    }
    // the top bar: what a pull does now
    g.fillStyle = 'rgba(10,14,22,.82)'; g.fillRect(0, 0, W, 64);
    g.fillStyle = '#e2e8f0'; g.font = `600 26px ${FONT}`; g.textAlign = 'left'; g.textBaseline = 'middle';
    const combat = c.combatOn();
    const note = this._status || (combat ? t('player.vr.map.combat')
      : m ? t('player.vr.map.markAgain')
        : pm && pm.spots.length ? t('player.vr.map.spots')
          : t('player.vr.map.point'));
    g.fillText(note, 20, 33, W - 120);
    p.drawButton('close', W - 84, 6, 74, 52, '×', { size: 40 });
    // the right column: zoom, me, layer
    const bx = W - 92, bw = 76;
    p.drawButton('zin', bx, 86, bw, 76, '＋', { size: 44 });
    p.drawButton('zout', bx, 172, bw, 76, '－', { size: 44 });
    p.drawButton('me', bx, 258, bw, 76, '◎', { size: 40, off: !c.self() });
    p.drawButton('layer', bx, 344, bw, 76, mv.streets ? '🛰' : '🛣', { size: 36 });
    // the bottom bar: the target + ⤳ Beam here
    if (m) {
      g.fillStyle = 'rgba(10,14,22,.82)'; g.fillRect(0, H - 92, W, 92);
      const s = c.self(), y = c.groundY(m.x, m.z);
      const bits = [];
      if (s) {
        const d = Math.hypot(m.x - s.x, m.z - s.z);
        bits.push(d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`);
        const brg = (Math.atan2(m.x - s.x, s.z - m.z) * 180 / Math.PI + 360) % 360;
        bits.push(`${Math.round(brg)}°`);
      }
      if (y !== null && y !== undefined && Number.isFinite(y)) bits.push(t('player.vr.map.asl', { m: Math.round(y) }));
      g.fillStyle = '#e2e8f0'; g.font = `600 28px ${FONT}`; g.textAlign = 'left';
      g.fillText(bits.join(' · '), 24, H - 46);
      p.drawButton('unmark', W - 520, H - 80, 150, 68, t('player.vr.map.clear'), { size: 30 });
      p.drawButton('beam', W - 356, H - 80, 340, 68, this._beaming ? t('player.vr.status.beaming') : t('player.vr.map.beamHere'),
        { off: combat || this._beaming, sel: !combat && !this._beaming });
    }
    p.tex.needsUpdate = true;
    p.dirty = false;
  }
}

// The VR HUD: the desktop HUD's twin as two panels (compass tape, status
// line) in a group on the rig. The group sits at the head and turns with its
// yaw lazily: it starts to follow past HUD_FOLLOW_DEG and eases until within
// HUD_SETTLE_DEG, so a glance does not drag it. Facts come from main.js at its
// HUD cadence (setFacts); the compass reads the head every frame and repaints
// at most every COMPASS_MS, the status every STATUS_MS when its text changed.
// Drawn under the panels (renderOrder 20) and over the world (depthTest off).
class VrHud {
  constructor(ctx) {
    this.ctx = ctx;
    this.group = new THREE.Group();
    this.group.visible = false;
    ctx.rig.add(this.group);
    this.compass = new Panel(1024, 150, 0.8, 20);
    this.status = new Panel(1280, 240, 1.2, 20);
    const up = HUD_UP_DEG * Math.PI / 180, dn = -HUD_DOWN_DEG * Math.PI / 180;
    this.compass.mesh.position.set(0, HUD_D * Math.sin(up), -HUD_D * Math.cos(up));
    this.compass.mesh.rotation.x = up;           // faces the eyes
    this.status.mesh.position.set(0, HUD_D * Math.sin(dn), -HUD_D * Math.cos(dn));
    this.status.mesh.rotation.x = dn;
    this.compass.mesh.visible = this.status.mesh.visible = true;
    this.group.add(this.compass.mesh, this.status.mesh);
    // the key-hint row: its own transparent strip over the status panel's
    // keys line (same px scale), so a page fade is the material's opacity —
    // ONE repaint per page change (keyhints.js HintCycle: out, swap, in)
    this.keysP = new Panel(1280, 48, 1.2, 21);
    this.keysP.mesh.visible = true;
    this.status.mesh.add(this.keysP.mesh);
    this._kc = new HintCycle(khReduced() ? { cycleMs: KH.reducedMs, fadeMs: 0 } : {});
    this._kShown = -1; this._kSig = null;
    this.on = lsGet(LS_HUD) !== '0';
    this.yaw = null;            // the group's yaw (lazy)
    this._follow = false;
    this.bearing = null;        // painted bearing
    this._cAt = 0; this._sAt = 0;
    this.markers = [];          // [{brg, color}] (main.js, reused)
    this.primary = ''; this.cond = ''; this.zoom = 1;
    this.window = false;        // the head zoom window is up: the compass makes room
  }

  setOn(on) { this.on = !!on; lsSet(LS_HUD, this.on ? 1 : 0); if (!this.on) this.hide(); }
  hide() { this.group.visible = false; this.yaw = null; }

  // main.js, at the HUD cadence: the status rows (plain text) and the key-hint
  // pages (keyhints.js keyHintPages 'vr'; a changed set restarts the cycle)
  setFacts(primary, cond, keys) {
    if (primary !== this.primary || cond !== this.cond) {
      this.primary = primary; this.cond = cond;
      this.status.dirty = true;
    }
    this._kc.set(keys, performance.now());
  }

  update(now, dt) {
    const c = this.ctx;
    if (!this.on) return;
    const head = c.head, eye = c.eye && c.eye();
    if (!eye) return;
    // the head's yaw in the rig (rig-local camera) → the lazy group yaw
    _f.set(0, 0, -1).applyQuaternion(head.quaternion);
    const hy = Math.atan2(-_f.x, -_f.z);
    if (this.yaw === null) this.yaw = hy;
    let dy = hy - this.yaw;
    dy = Math.atan2(Math.sin(dy), Math.cos(dy));
    const deg = Math.abs(dy) * 180 / Math.PI;
    if (deg > HUD_FOLLOW_DEG) this._follow = true;
    if (this._follow) {
      this.yaw += dy * Math.min(1, dt * 4);
      if (deg < HUD_SETTLE_DEG) this._follow = false;
    }
    this.group.position.copy(head.position);
    this.group.rotation.set(0, this.yaw, 0);
    this.group.visible = true;
    this.compass.mesh.visible = !this.window;
    // the compass: the head's world bearing (x = East, −z = North)
    eye.getWorldDirection(_d);
    let b = Math.atan2(_d.x, -_d.z) * 180 / Math.PI;
    if (b < 0) b += 360;
    if (this.compass.mesh.visible && (this.bearing === null || Math.abs(((b - this.bearing + 540) % 360) - 180) >= 0.4)
      && now - this._cAt >= COMPASS_MS) {
      this._cAt = now; this.bearing = b;
      this._paintCompass();
    }
    const z = c.zoom ? Math.round(c.zoom() * 10) / 10 : 1;
    if (z !== this.zoom) { this.zoom = z; this.status.dirty = true; }
    if (this.status.dirty && now - this._sAt >= STATUS_MS) { this._sAt = now; this._paintStatus(); }
    // the key hints: the old page fades out over the first half of the fade,
    // the new one (repainted once, at the swap) in over the second
    const kc = this._kc.at(now);
    const show = kc.k < 0.5 ? kc.prev : kc.i;
    if (show !== this._kShown || this._kc.sig !== this._kSig) this._paintKeys(show);
    const a = kc.k >= 1 ? 1 : kc.k < 0.5 ? 1 - 2 * kc.k : 2 * kc.k - 1;
    const m = this.keysP.mesh.material;
    if (m.opacity !== a) m.opacity = a;
  }

  _paintKeys(i) {
    const p = this.keysP, g = p.g;
    this._kShown = i; this._kSig = this._kc.sig;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, p.wPx, p.hPx);
    const txt = this._kc.pages[i] || '';
    if (txt) {
      g.fillStyle = 'rgba(203,213,225,.85)'; g.font = `500 28px ${FONT}`;
      g.textAlign = 'left'; g.textBaseline = 'middle';
      g.fillText(txt, 28, p.hPx / 2, p.wPx - 56);
    }
    p.tex.needsUpdate = true;
  }

  _paintCompass() {
    const p = this.compass, g = p.g;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, p.wPx, p.hPx);
    paintCompass(g, 8, 4, p.wPx - 16, 3, this.bearing, this.markers);
    p.tex.needsUpdate = true;
    p.dirty = false;
  }

  _paintStatus() {
    const p = this.status, g = p.g, W = p.wPx;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, W, p.hPx);
    // the key hints: ONE line (keyhints.js pages fit it), drawn by keysP
    const H = 24 + 56 + (this.cond ? 46 : 0) + 38 + 12;
    roundRect(g, 4, p.hPx - H, W - 8, H - 4, 22);
    g.fillStyle = 'rgba(6,10,16,.62)'; g.fill();
    let y = p.hPx - H + 50;
    g.textBaseline = 'middle';
    g.textAlign = 'left'; g.fillStyle = '#f1f5f9'; g.font = `700 44px ${FONT}`;
    g.fillText(this.primary, 28, y, W - 220);
    // the zoom, right: gold while magnified
    g.textAlign = 'right'; g.fillStyle = this.zoom > 1 ? '#ffc93c' : 'rgba(226,232,240,.7)';
    g.font = '700 44px ui-monospace, monospace';
    g.fillText(`${this.zoom.toFixed(this.zoom < 10 ? 1 : 0)}×`, W - 28, y);
    g.textAlign = 'left';
    if (this.cond) {
      y += 50;
      g.fillStyle = '#e2e8f0'; g.font = `600 32px ${FONT}`;
      g.fillText(this.cond, 28, y, W - 56);
    }
    y += 46;
    // keysP sits on this line (a child of the status mesh: canvas px → metres)
    this.keysP.mesh.position.set(0, (p.hPx / 2 - y) * p.hM / p.hPx, 0);
    p.tex.needsUpdate = true;
    p.dirty = false;
  }
}
